import { fetchCustomerApplicationTemplates } from "Hooks/Api/apiCustomerApplications";
import { UpdateUserApplicationTemplates } from "Hooks/Api/apiUsers";
import useAsync from "Hooks/useAsync";
import { isEqual } from "lodash";
import { useState } from "react";
import alertToast from "Util/ToastifyUtils";
import { Colors, Display } from "../Button/Button";
import ButtonAsyncState from "../Button/ButtonAsyncState";
import { ComboboxOption } from "../Combobox/Combobox";
import MultiComboboxSetter from "../Combobox/MultiComboboxSetter";

type Props = {
	userId: string;
	filterByBusinessIds: string[];
}
export const CustomerApplicationTemplateSelector: React.FunctionComponent<Props> = ({
	userId,
	filterByBusinessIds,
}) => {
	const [applicationTemplateIds, setApplicationTemplates] = useState<string[]>([]);

	const { data, refresh } = useAsync(async () => {
		const selectedTemplates = await fetchCustomerApplicationTemplates({
			filterByBusinessIds,
			filterByUserIds: [userId],
		});
		const availableTemplates = await fetchCustomerApplicationTemplates({
			filterByBusinessIds,
		});
		setApplicationTemplates(selectedTemplates.data.data.map(template => template.id));
		return {
			availableTemplates: availableTemplates.data.data,
			selectedTemplates: selectedTemplates.data.data,
		}
	}, []);

	const availableOptions: ComboboxOption<string>[] = data?.availableTemplates.map(template => ({
		display: template.applicationName,
		value: template.id,
	})) || [];

	const handleReadOnly = () => {
		const originalSelectedTemplates = data?.selectedTemplates || [];
		return isEqual(originalSelectedTemplates.map(template => template.id), applicationTemplateIds);
	}

	const handleSaveChanges = async () => {
		try {
			await UpdateUserApplicationTemplates(userId, applicationTemplateIds);
			alertToast('Customer application templates updated successfully', 'success');
			refresh(); // Refresh the data to reflect changes
		} catch (error) {
			console.error('Error updating customer application templates:', error);
			alertToast('Failed to update customer application templates', 'error');
		}
	}

	return (
		<>
			<MultiComboboxSetter
				value={applicationTemplateIds}
				setValue={setApplicationTemplates}
				options={availableOptions}
				label="Customer Application Templates"
				placeholder={availableOptions.length > 0 ? undefined : 'No customer application templates are available for this user.'}
				getOptionValue={value => value}
				tooltip='Select the customer application templates that this user can access. These templates will be used when creating new customer applications.'
				searchable
				isClearable
			/>
			<div className="button-container">
				<ButtonAsyncState
					className="save"
					colors={Colors.Primary}
					display={Display.Solid}
					onPress={handleSaveChanges}
					readonly={handleReadOnly()}
					waitingText={'Saving Changes...'}
				>
					Save Changes
				</ButtonAsyncState>
			</div>
		</>
	);
}